import { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Slider, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import LooksOneOutlinedIcon from '@mui/icons-material/LooksOneOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import { useSignalCount } from './signalsService'
import { C, Card, CountUp, IconBubble, InfoTip, Pill, Reveal, SectionHead, Source, floatY, grad, growX, useInView } from './aboutUi'

// ─────────────────────────────────────────────────────────────────────────────
// Levers: which business KPI each PACE capability moves.
// ─────────────────────────────────────────────────────────────────────────────
const LEVERS = [
  { t: 'Fewer RTOs', k: 'RTO %', dir: 'down', icon: <AssignmentReturnOutlinedIcon />, c: C.rose,
    d: 'Risky couriers for COD and high-value orders are avoided on the lanes where they fail.',
    tip: 'PACE predicts the chance each courier returns this specific order, so it steers COD and high-value orders to the couriers that actually convert in that area. Couriers below the delivery guardrail are never picked.' },
  { t: 'More first-attempt deliveries', k: 'FASR %', dir: 'up', icon: <LooksOneOutlinedIcon />, c: C.violet,
    d: 'Fewer "customer not available" loops, fewer NDRs, happier buyers.',
    tip: 'First-attempt success is scored directly, at 20% of the default score. Every failed attempt that is avoided removes a day of delay, an NDR call and part of the RTO risk.' },
  { t: 'On-time, every time', k: 'SLA breaches', dir: 'down', icon: <EventAvailableOutlinedIcon />, c: C.sky,
    d: 'Same-day and next-day promises go to couriers that keep them, and disruptions are routed around.',
    tip: 'The service-tier ladder makes sure the promised SLA is honoured first. On-time adherence is part of the score, and logged disruptions (floods, strikes, festival backlogs) push the affected couriers\' ETA back at allocation time.' },
  { t: 'Lower true shipping cost', k: '₹ / order', dir: 'down', icon: <CurrencyRupeeIcon />, c: C.emerald,
    d: 'Compares rate card plus expected weight-dispute surcharge, not just list price.',
    tip: 'Where couriers are equally good, PACE prefers the one that is really cheaper once the surcharges it tends to add later are included. The speed ↔ cost dial lets each brand decide how much cost should count.' },
  { t: 'Promises customers trust', k: 'Broken EDDs', dir: 'down', icon: <FavoriteBorderOutlinedIcon />, c: C.amber,
    d: 'Paired with PRISM iEDD, the date shown at checkout is one you actually meet.',
    tip: 'PRISM, PACE\'s companion model, predicts a delivery date Prozo can stand behind for each order. It is calibrated so that 95 or more out of 100 orders arrive by that date.' },
  { t: 'Less ops firefighting', k: 'Team hours', dir: 'down', icon: <SupportAgentOutlinedIcon />, c: C.teal,
    d: 'No manual rule tuning. Every decision is explained, so questions are answered in seconds.',
    tip: 'The models refresh themselves from real outcomes every night and every week. When someone asks "why did this order go to courier X?", the explainer shows the answer.' },
]

// ─────────────────────────────────────────────────────────────────────────────
// Accuracy rings (measured, out-of-time validation).
// ─────────────────────────────────────────────────────────────────────────────
function Ring({ value, color, label }) {
  const [ref, seen] = useInView(0.4)
  const r = 52
  const circ = 2 * Math.PI * r
  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <Box component='svg' viewBox='0 0 130 130' sx={{ width: 150, height: 150 }}>
        <circle cx='65' cy='65' r={r} fill='none' stroke={C.line} strokeWidth='12' />
        <circle cx='65' cy='65' r={r} fill='none' stroke={color} strokeWidth='12' strokeLinecap='round'
          strokeDasharray={circ} strokeDashoffset={seen ? circ * (1 - value) : circ} transform='rotate(-90 65 65)'
          style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(.2,.8,.2,1)' }} />
        <text x='65' y='72' textAnchor='middle' fontSize='26' fontWeight='900' fill={C.slate}>{Math.round(value * 100)}%</text>
      </Box>
      <Typography sx={{ fontWeight: 800, color: C.slate }}>{label}</Typography>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Promise reliability: courier-declared EDD vs PRISM iEDD (backtest).
// ─────────────────────────────────────────────────────────────────────────────
function PromiseBars() {
  const [ref, seen] = useInView(0.4)
  const rows = [
    { t: 'Courier-declared delivery date', v: 82.5, c: '#94A3B8' },
    { t: 'PRISM iEDD (Prozo AI date)', v: 97.7, c: C.emerald },
  ]
  return (
    <Box ref={ref} sx={{ display: 'grid', gap: 2.5 }}>
      {rows.map((r) => (
        <Box key={r.t}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontWeight: 700, color: C.slate, fontSize: 14.5 }}>{r.t}</Typography>
            <Typography sx={{ fontWeight: 900, color: r.c === '#94A3B8' ? C.muted : C.emerald, fontSize: 18 }}>{r.v}%</Typography>
          </Box>
          <Box sx={{ height: 22, borderRadius: 11, background: C.line, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: seen ? `${r.v}%` : 0, background: r.c === '#94A3B8' ? r.c : grad(C.teal, C.emerald), borderRadius: 11, transition: 'width 1.4s cubic-bezier(.2,.8,.2,1)' }} />
          </Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: 3, background: '#F1F5F9', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 26, color: C.muted }}>~1 in 6</Typography>
          <Typography sx={{ fontSize: 13, color: C.muted }}>courier dates broken</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: 3, background: '#ECFDF5', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 26, color: C.emerald }}>~1 in 40</Typography>
          <Typography sx={{ fontSize: 13, color: C.muted }}>PRISM dates broken</Typography>
        </Box>
      </Box>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ROI calculator: purely the merchant's inputs × an assumed improvement.
// ─────────────────────────────────────────────────────────────────────────────
const INR = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`
const compactINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`
  return INR(n)
}

function CalcSlider({ label, value, onChange, min, max, step, fmt, tip, color = C.indigo }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: C.ink, fontSize: 14, display: 'flex', alignItems: 'center' }}>
          {label}{tip && <InfoTip title={label} color={color}><p>{tip}</p></InfoTip>}
        </Typography>
        <Typography sx={{ fontWeight: 900, color, fontSize: 16 }}>{fmt(value)}</Typography>
      </Box>
      <Slider value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v)} sx={{ color, py: 1.25 }} />
    </Box>
  )
}

function RoiCalculator() {
  const [orders, setOrders] = useState(50000)
  const [codShare, setCodShare] = useState(60)
  const [codRto, setCodRto] = useState(25)
  const [ppRto, setPpRto] = useState(7)
  const [rtoCost, setRtoCost] = useState(180)
  const [aov, setAov] = useState(1200)
  const [lift, setLift] = useState(10)

  const rtoNow = orders * (codShare / 100) * (codRto / 100) + orders * (1 - codShare / 100) * (ppRto / 100)
  const avoided = rtoNow * (lift / 100)
  const saved = avoided * rtoCost
  const gmv = avoided * aov
  const blended = orders ? (rtoNow / orders) * 100 : 0
  const blendedAfter = orders ? ((rtoNow - avoided) / orders) * 100 : 0

  return (
    <Card hover={false} sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.1fr .9fr' } }}>
        <Box sx={{ p: { xs: 2.5, md: 4 }, display: 'grid', gap: 1 }}>
          <Typography sx={{ fontWeight: 900, color: C.slate, fontSize: 18, mb: 1 }}>Your numbers</Typography>
          <CalcSlider label='Orders per month' value={orders} onChange={setOrders} min={1000} max={500000} step={1000} fmt={(v) => v.toLocaleString('en-IN')} />
          <CalcSlider label='COD share' value={codShare} onChange={setCodShare} min={0} max={100} step={1} fmt={(v) => `${v}%`} color={C.orange} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <CalcSlider label='COD RTO today' value={codRto} onChange={setCodRto} min={0} max={50} step={0.5} fmt={(v) => `${v}%`} color={C.rose}
              tip='Across Prozo’s network, forward COD RTO has averaged about 28%. Use your own number if you know it.' />
            <CalcSlider label='Prepaid RTO today' value={ppRto} onChange={setPpRto} min={0} max={25} step={0.5} fmt={(v) => `${v}%`} color={C.rose}
              tip='Across Prozo’s network, forward prepaid RTO has averaged about 7%.' />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <CalcSlider label='Cost of one RTO' value={rtoCost} onChange={setRtoCost} min={50} max={1000} step={10} fmt={INR} color={C.violet}
              tip='Direct cost only: return freight plus handling. ₹180 is PACE’s own default. Many brands add packaging, QC and stuck-inventory cost, which makes the figure higher.' />
            <CalcSlider label='Average order value' value={aov} onChange={setAov} min={200} max={10000} step={50} fmt={INR} color={C.sky}
              tip='Used to show the sales value of the extra orders that now reach customers instead of coming back.' />
          </Box>
          <Box sx={{ mt: 1, p: 2, borderRadius: 3, background: '#EEF2FF', border: `1px dashed ${C.indigo}66` }}>
            <CalcSlider label='Assumed RTO reduction with PACE' value={lift} onChange={setLift} min={0} max={30} step={1} fmt={(v) => `${v}%`}
              tip='A relative reduction: 10% means 25% RTO becomes 22.5%. This is an assumption, not a guarantee. A shadow run measures the real number on your own orders.' />
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2.5, md: 4 }, color: '#fff', background: 'linear-gradient(160deg, #064E3B, #047857 45%, #0D9488)', position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,.07)', right: -60, top: -60, animation: `${floatY} 6s ease-in-out infinite` }} />
          <Typography sx={{ fontWeight: 800, opacity: 0.85, fontSize: 13, letterSpacing: 1.5 }}>ESTIMATED MONTHLY IMPACT</Typography>
          <Typography sx={{ fontSize: { xs: 40, md: 52 }, fontWeight: 900, lineHeight: 1.1, mt: 1 }}>{compactINR(saved)}</Typography>
          <Typography sx={{ opacity: 0.85 }}>direct RTO cost saved every month</Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 3 }}>
            {[
              { v: Math.round(avoided).toLocaleString('en-IN'), t: 'RTOs avoided / month' },
              { v: compactINR(saved * 12), t: 'saved per year' },
              { v: compactINR(gmv), t: 'extra orders reaching customers / month' },
              { v: `${blended.toFixed(1)}% → ${blendedAfter.toFixed(1)}%`, t: 'blended RTO rate' },
            ].map((x) => (
              <Box key={x.t} sx={{ p: 1.75, borderRadius: 3, background: 'rgba(255,255,255,.12)', backdropFilter: 'blur(4px)' }}>
                <Typography sx={{ fontWeight: 900, fontSize: 19 }}>{x.v}</Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{x.t}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 3, fontStyle: 'italic' }}>
            Illustrative only: your inputs × an assumed improvement. It does not include the savings from fewer re-attempts, fewer broken promises or lower surcharges. Measure the real number with a shadow run on your own orders.
          </Typography>
        </Box>
      </Box>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Adoption journey
// ─────────────────────────────────────────────────────────────────────────────
const JOURNEY = [
  { t: 'Shadow run', icon: <ScienceOutlinedIcon />, c: C.sky, tag: 'Zero risk',
    d: 'PACE decides on every order in parallel with your current allocation, but changes nothing. You get a side-by-side of what PACE would have picked, and why.' },
  { t: 'Live pilot', icon: <RocketLaunchOutlinedIcon />, c: C.violet, tag: 'You choose the scope',
    d: 'Switch PACE live on your account. If PACE is ever slow or unavailable, orders fall back to the existing engine automatically.' },
  { t: 'Scale & tune', icon: <FavoriteBorderOutlinedIcon />, c: C.emerald, tag: 'Your goals, your knobs',
    d: 'Roll out across the business. Tune speed vs cost per merchant, set same-day or next-day service tiers by zone and time, and let the models keep learning.' },
]

function Journey() {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', top: 44, left: '16%', right: '16%', height: 4, borderRadius: 2, background: C.line, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', background: `linear-gradient(90deg, ${C.sky}, ${C.violet}, ${C.emerald})`, transformOrigin: 'left', animation: `${growX} 2.2s ease both` }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, position: 'relative' }}>
        {JOURNEY.map((j, i) => (
          <Reveal key={j.t} delay={i * 180}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: 88, height: 88, borderRadius: '50%', display: 'grid', placeItems: 'center', background: '#fff', border: `4px solid ${j.c}`, color: j.c, boxShadow: `0 12px 30px ${j.c}44`, '& svg': { fontSize: 40 } }}>
                  {j.icon}
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: j.c, mt: 2, letterSpacing: 1.5 }}>PHASE {i + 1}</Typography>
              <Typography sx={{ fontWeight: 900, color: C.slate, fontSize: 20 }}>{j.t}</Typography>
              <Box sx={{ mt: 1 }}><Pill color={j.c}>{j.tag}</Pill></Box>
              <Typography sx={{ color: C.muted, fontSize: 14.5, mt: 1.5, maxWidth: 330, mx: 'auto' }}>{j.d}</Typography>
            </Box>
          </Reveal>
        ))}
      </Box>
      <Reveal delay={400}>
        <Box sx={{ mt: 4, mx: 'auto', maxWidth: 640, p: 2, borderRadius: 99, background: '#F0FDFA', border: `1px solid ${C.teal}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <SettingsBackupRestoreIcon sx={{ color: C.teal }} />
          <Typography sx={{ fontWeight: 700, color: C.slate, fontSize: 14.5 }}>Instant rollback at every phase: one switch per merchant.</Typography>
        </Box>
      </Reveal>
    </Box>
  )
}

const FAQ = [
  { q: 'Is PACE a black box?', a: 'No. Every ranking comes with an explainer showing how much delivery success, first-attempt success, on-time performance and cost each contributed, and whether each number came from the courier’s recent track record or from an AI prediction.' },
  { q: 'Does PACE replace our courier rules or contracts?', a: 'No. Your rules and serviceability still decide which couriers are eligible, and each courier is priced from its own rate card. PACE only ranks the eligible couriers to pick the best one.' },
  { q: 'What if the AI gets it wrong, or goes down?', a: 'Couriers that are too risky are blocked by a delivery guardrail. If PACE errors or times out, the order is allocated by the existing engine automatically. Any merchant can be switched back with one toggle.' },
  { q: 'Can we control cost vs speed?', a: 'Yes. Business knobs set how much delivery, first attempt, on-time and cost count, plus a speed ↔ cost dial. They can be set globally or per merchant, apply within a minute, and every change is audited.' },
  { q: 'How does it handle same-day / next-day promises?', a: 'Service tiers such as SDD, NDD and Surface are configured by merchant, zone and time window, and PACE always fills the promised tier first. It steps to the next tier only if none is available, and it never silently upsells to a faster, costlier tier.' },
  { q: 'How quickly does it adapt when a courier’s performance drops?', a: 'Courier track records refresh every night, with recent days weighted more heavily, and the models retrain weekly behind a quality gate. Logged disruptions such as floods, strikes and festival backlogs affect the ETA straight away.' },
  { q: 'How do we know it works for us before committing?', a: 'Start with a shadow run. PACE makes a decision on every one of your orders without changing anything, so you can compare its picks against your current allocation before going live.' },
]

export default function ImpactTab() {
  const nSignals = useSignalCount()
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 7 }}>
      <Box>
        <Reveal>
          <SectionHead kicker='Where the value comes from' color={C.emerald} title='Six business KPIs PACE moves' sub='Tap the (i) on any lever to see how PACE moves it.' />
        </Reveal>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {LEVERS.map((l, i) => (
            <Reveal key={l.t} delay={(i % 3) * 100}>
              <Card accent={l.c} sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <IconBubble icon={l.icon} color={l.c} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Pill color={l.dir === 'down' ? C.emerald : C.emerald}>
                      {l.dir === 'down' ? <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} /> : <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />}
                      {l.k}
                    </Pill>
                    <InfoTip title={l.t} color={l.c}><p>{l.tip}</p></InfoTip>
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 900, color: C.slate, mt: 2, fontSize: 18 }}>{l.t}</Typography>
                <Typography sx={{ color: C.muted, fontSize: 14.5, mt: 0.75 }}>{l.d}</Typography>
              </Card>
            </Reveal>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '1fr 1fr' }, gap: 3 }}>
        <Reveal>
          <Card hover={false} sx={{ height: '100%' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.indigo, letterSpacing: 2 }}>PROOF · THE AI IS ACCURATE</Typography>
            <Typography sx={{ fontWeight: 900, color: C.slate, fontSize: 22, mt: 0.5, display: 'flex', alignItems: 'center' }}>
              It spots risky shipments before they ship
              <InfoTip title='How to read these numbers'>
                <p>Take one order that was eventually returned and one that was delivered. The RTO model correctly flags the returned one as riskier <b>84%</b> of the time. A coin flip would score 50%.</p>
                <p>The delivery model does the same for &quot;will it be delivered&quot; at <b>81%</b>.</p>
                <p>These were measured on a month of shipments the models had never seen during training.</p>
              </InfoTip>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2, mt: 3 }}>
              <Ring value={0.84} color={C.rose} label='RTO risk model' />
              <Ring value={0.81} color={C.emerald} label='Delivery model' />
            </Box>
            <Box sx={{ mt: 2.5, p: 2, borderRadius: 3, background: '#EEF2FF', display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <VisibilityOutlinedIcon sx={{ color: C.indigo }} />
              <Typography sx={{ fontSize: 14, color: C.ink }}>
                <b>Honest probabilities:</b> on average, predicted chances land within about 4 percentage points of what really happened. When PACE says 90%, you can plan on 90%.
              </Typography>
            </Box>
            <Source>Out-of-time validation on June 2026 shipments (ranking accuracy / AUC and calibration error)</Source>
          </Card>
        </Reveal>
        <Reveal delay={120}>
          <Card hover={false} sx={{ height: '100%' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.teal, letterSpacing: 2 }}>PROOF · PROMISES YOU CAN KEEP</Typography>
            <Typography sx={{ fontWeight: 900, color: C.slate, fontSize: 22, mt: 0.5, mb: 3, display: 'flex', alignItems: 'center' }}>
              Delivery dates met, courier vs PRISM
              <InfoTip title='What is PRISM iEDD?' color={C.teal}>
                <p>PRISM is PACE&apos;s companion model. It predicts the delivery date for each order from pickup, transit and last-mile patterns, then calibrates it per courier and per merchant.</p>
                <p>The date can be slightly later than a courier&apos;s optimistic promise, but it is one the customer can rely on. That means fewer &quot;where is my order?&quot; calls and fewer cancellations.</p>
              </InfoTip>
            </Typography>
            <PromiseBars />
            <Source>PRISM backtest on ~975K delivered Prozo shipments</Source>
          </Card>
        </Reveal>
      </Box>

      <Box>
        <Reveal>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2.5 }}>
            {[
              { v: <CountUp key={nSignals} to={nSignals} />, t: 'signals read for every order', c: C.violet },
              { v: <CountUp to={4} />, t: 'AI predictions per courier', c: C.indigo },
              { v: '< 1 s', t: 'to rank every eligible courier', c: C.sky },
              { v: <CountUp to={60} suffix=' s' />, t: 'for a knob change to go live, with no retraining', c: C.amber },
            ].map((x) => (
              <Card key={x.t} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 900, color: x.c }}>{x.v}</Typography>
                <Typography sx={{ color: C.muted, fontSize: 13.5 }}>{x.t}</Typography>
              </Card>
            ))}
          </Box>
        </Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Value calculator' color={C.emerald} title='What could fewer RTOs be worth to you?'
            sub='Enter your own numbers. Every figure is transparent and editable.' />
        </Reveal>
        <Reveal delay={100}><RoiCalculator /></Reveal>
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='Getting started' color={C.violet} title='From zero risk to full value in three phases' align='center' />
        </Reveal>
        <Journey />
      </Box>

      <Box>
        <Reveal>
          <SectionHead kicker='FAQ' color={C.indigo} title='Frequently asked questions' />
        </Reveal>
        <Reveal delay={100}>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {FAQ.map((f, i) => (
              <Accordion key={f.q} disableGutters defaultExpanded={i === 0} sx={{ borderRadius: '14px !important', border: `1px solid ${C.line}`, boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: C.indigo }} />}>
                  <Typography sx={{ fontWeight: 800, color: C.slate }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails><Typography sx={{ color: C.ink, fontSize: 14.5 }}>{f.a}</Typography></AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Reveal>
      </Box>

      <Reveal>
        <Box sx={{
          p: { xs: 3, md: 5 }, borderRadius: 5, color: '#fff', textAlign: 'center',
          background: 'linear-gradient(120deg, #312E81, #4F46E5 40%, #0D9488)', boxShadow: '0 24px 60px rgba(79,70,229,.3)',
        }}>
          <Typography sx={{ fontSize: { xs: 24, md: 34 }, fontWeight: 900 }}>See PACE on your own orders</Typography>
          <Typography sx={{ opacity: 0.9, mt: 1, maxWidth: 640, mx: 'auto' }}>
            Start with a shadow run. PACE decides on every order alongside your current setup and changes nothing, so the results speak for themselves.
          </Typography>
        </Box>
      </Reveal>
    </Box>
  )
}
